import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Plus, AlertCircle, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AddPosteDepenseModal = ({ 
  isOpen, 
  onClose, 
  coproprietaires, 
  existingPostes,
  onSubmit,
  isSubmitting 
}) => {
  const [step, setStep] = useState(1);
  const [posteData, setPosteData] = useState({
    nom: '',
    description: ''
  });
  const [repartition, setRepartition] = useState({});
  const [errors, setErrors] = useState({});

  // Initialiser la répartition avec les tantièmes du premier poste existant
  useEffect(() => {
    if (isOpen && coproprietaires.length > 0) {
      const initialRepartition = {};
      const premierPoste = existingPostes?.[0];
      
      coproprietaires.forEach(copro => {
        // Utiliser les tantièmes du premier poste comme valeur par défaut
        const defaultTantiemes = premierPoste?.repartition?.[copro.id] || copro.tantiemes || 0;
        initialRepartition[copro.id] = defaultTantiemes;
      });
      
      setRepartition(initialRepartition);
    }
  }, [isOpen, coproprietaires, existingPostes]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setPosteData({ nom: '', description: '' });
      setRepartition({});
      setErrors({});
    }
  }, [isOpen]);

  const totalTantiemes = Object.values(repartition).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const isValidTotal = Math.abs(totalTantiemes - 1000) < 0.01;

  const handleStep1Submit = () => {
    const newErrors = {};
    if (!posteData.nom.trim()) {
      newErrors.nom = 'Le nom du poste est requis';
    }
    
    // Vérifier que le nom n'existe pas déjà
    const exists = existingPostes?.some(p => p.nom.toLowerCase() === posteData.nom.toLowerCase().trim());
    if (exists) {
      newErrors.nom = 'Un poste avec ce nom existe déjà';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setStep(2);
  };

  const handleRepartitionChange = (userId, value) => {
    setRepartition(prev => ({
      ...prev,
      [userId]: parseFloat(value) || 0
    }));
  };

  const handleSubmit = () => {
    if (!isValidTotal) return;
    
    onSubmit({
      nom: posteData.nom.trim(),
      description: posteData.description.trim(),
      repartition: repartition
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Plus className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Nouveau poste de dépenses
              </h2>
              <p className="text-sm text-slate-500">
                Étape {step}/2 — {step === 1 ? 'Informations' : 'Répartition des tantièmes'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-slate-200">
          <div 
            className="h-full bg-emerald-500 transition-all duration-300"
            style={{ width: step === 1 ? '50%' : '100%' }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nom du poste de dépenses *
                </label>
                <input
                  type="text"
                  value={posteData.nom}
                  onChange={(e) => setPosteData(prev => ({ ...prev, nom: e.target.value }))}
                  placeholder="Ex: Ascenseur, Chauffage collectif, Entretien espaces verts..."
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all ${
                    errors.nom ? 'border-red-500 bg-red-50' : 'border-slate-300'
                  }`}
                  data-testid="poste-nom-input"
                />
                {errors.nom && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.nom}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description (optionnelle)
                </label>
                <textarea
                  value={posteData.description}
                  onChange={(e) => setPosteData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Décrivez ce poste de dépenses..."
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                  data-testid="poste-description-input"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <strong>Conseil :</strong> Créez des postes distincts pour les dépenses qui ne concernent pas tous les copropriétaires 
                  (ex: ascenseur uniquement pour certains bâtiments, chauffage collectif, etc.)
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Important :</strong> Le total des tantièmes pour ce poste doit être exactement <strong>1000</strong>.
                  Les tantièmes sont pré-remplis avec les valeurs existantes.
                </p>
              </div>

              {/* Indicateur du total */}
              <div className={`sticky top-0 z-10 p-4 rounded-xl border ${
                isValidTotal 
                  ? 'bg-emerald-50 border-emerald-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total des tantièmes</span>
                  <span className={`text-2xl font-bold ${
                    isValidTotal ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {totalTantiemes.toFixed(2)} / 1000
                  </span>
                </div>
                {!isValidTotal && (
                  <p className="text-sm text-red-600 mt-1">
                    {totalTantiemes < 1000 
                      ? `Il manque ${(1000 - totalTantiemes).toFixed(2)} tantièmes`
                      : `Excès de ${(totalTantiemes - 1000).toFixed(2)} tantièmes`
                    }
                  </p>
                )}
              </div>

              {/* Liste des copropriétaires */}
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Copropriétaire</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Lot</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700 w-40">Tantièmes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {coproprietaires.map((copro) => (
                      <tr key={copro.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900">
                              {copro.prenom} {copro.nom}
                            </p>
                            {copro.email && (
                              <p className="text-sm text-slate-500">{copro.email}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {copro.lot || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="1000"
                            value={repartition[copro.id] || ''}
                            onChange={(e) => handleRepartitionChange(copro.id, e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-right focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            data-testid={`tantiemes-${copro.id}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {coproprietaires.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  Aucun copropriétaire trouvé
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-slate-50">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button 
                onClick={handleStep1Submit}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                data-testid="poste-next-step"
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={() => setStep(1)}
                className="gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Retour
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={!isValidTotal || isSubmitting}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2 disabled:opacity-50"
                data-testid="poste-submit"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Créer le poste
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddPosteDepenseModal;
